import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  FormControlLabel,
  Switch,
  IconButton,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add,
  Edit,
  Delete,
  Search,
  School,
  Visibility,
  VisibilityOff,
  Star,
} from '@mui/icons-material';
import adminService from '../services/adminService';
import { formatDate } from '../utils/helpers';

const SpecializationManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpecialization, setEditingSpecialization] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadSpecializations();
  }, [searchTerm]);

  const loadSpecializations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;

      const response = await adminService.getSpecializations(params);
      setSpecializations(response.results || response);
    } catch (error) {
      console.error('Error loading specializations:', error);
      setError('שגיאה בטעינת התמחויות');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (specialization = null) => {
    if (specialization) {
      setEditingSpecialization(specialization);
      setFormData({
        name: specialization.name,
        description: specialization.description || '',
        is_active: specialization.is_active,
      });
    } else {
      setEditingSpecialization(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSpecialization(null);
    setFormData({ name: '', description: '', is_active: true });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name.trim()) {
        setError('שם ההתמחות הוא שדה חובה');
        return;
      }

      let response;
      if (editingSpecialization) {
        response = await adminService.updateSpecialization(editingSpecialization.id, formData);
        setSpecializations(specializations.map(spec => 
          spec.id === editingSpecialization.id ? response : spec
        ));
        setSuccess('התמחות עודכנה בהצלחה');
      } else {
        response = await adminService.createSpecialization(formData);
        setSpecializations([...specializations, response]);
        setSuccess('התמחות נוצרה בהצלחה');
      }
      
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving specialization:', error);
      setError('שגיאה בשמירת התמחות');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את ההתמחות?')) {
      return;
    }

    try {
      await adminService.deleteSpecialization(id);
      setSpecializations(specializations.filter(spec => spec.id !== id));
      setSuccess('התמחות נמחקה בהצלחה');
    } catch (error) {
      console.error('Error deleting specialization:', error);
      setError('שגיאה במחיקת התמחות');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const columns = [
    {
      field: 'id',
      headerName: 'מזהה',
      width: isMobile ? 60 : 80,
    },
    {
      field: 'name',
      headerName: 'שם התמחות',
      width: isMobile ? 150 : 200,
      flex: isMobile ? undefined : 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Star sx={{ color: 'warning.main', fontSize: '1rem' }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: 'text.primary'
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'תיאור',
      width: isMobile ? 200 : 300,
      flex: isMobile ? undefined : 1,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: isMobile ? '150px' : '250px'
          }}
          title={params.value}
        >
          {params.value || 'אין תיאור'}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'סטטוס',
      width: isMobile ? 80 : 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'פעיל' : 'לא פעיל'}
          size="small"
          color={params.value ? 'success' : 'default'}
          icon={params.value ? <Visibility /> : <VisibilityOff />}
          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'פעולות',
      width: isMobile ? 120 : 150,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="עריכה">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                },
              }}
            >
              <Edit fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Tooltip>
          <Tooltip title="מחיקה">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row.id)}
              sx={{
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'white',
                },
              }}
            >
              <Delete fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: isMobile ? 2 : 4, 
        mb: isMobile ? 2 : 4,
        px: isMobile ? 1 : 3,
      }}
    >
      <Typography 
        variant={isMobile ? "h5" : "h4"} 
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: 'primary.main',
          mb: isMobile ? 2 : 3,
          textAlign: isMobile ? 'center' : 'right',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMobile ? 'center' : 'flex-start',
          gap: 1
        }}
      >
        <School sx={{ fontSize: isMobile ? '1.5rem' : '2rem' }} />
        ניהול התמחויות
      </Typography>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            borderRadius: 2,
            fontSize: isMobile ? '0.875rem' : '1rem'
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 2,
            borderRadius: 2,
            fontSize: isMobile ? '0.875rem' : '1rem'
          }} 
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {/* Actions Bar */}
      <Paper 
        sx={{ 
          p: isMobile ? 1.5 : 2, 
          mb: 3,
          borderRadius: 2,
          boxShadow: theme.shadows[2],
        }}
      >
        <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              placeholder="חיפוש התמחויות"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end' }}>
              <Button
                variant="contained"
                size={isMobile ? 'medium' : 'large'}
                startIcon={<Add />}
                onClick={() => handleOpenDialog()}
                sx={{
                  borderRadius: 2,
                  px: isMobile ? 2 : 3,
                  py: isMobile ? 1 : 1.5,
                  fontWeight: 'bold',
                  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.6)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease-in-out',
                }}
              >
                {isMobile ? 'הוסף' : 'הוסף התמחות'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Specializations Table */}
      <Paper 
        sx={{ 
          height: isMobile ? 500 : 600, 
          width: '100%',
          borderRadius: 2,
          boxShadow: theme.shadows[3],
        }}
      >
        <DataGrid
          rows={specializations}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          loading={loading}
          disableSelectionOnClick
          rowHeight={isMobile ? 60 : 70}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              fontSize: isMobile ? '0.8rem' : '0.875rem',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              borderBottom: '2px solid #e0e0e0',
              fontWeight: 'bold',
              fontSize: isMobile ? '0.8rem' : '0.875rem',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            },
            // Hide description column on mobile
            '@media (max-width: 768px)': {
              '& .MuiDataGrid-columnHeader[data-field="description"]': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="description"]': {
                display: 'none',
              },
            },
          }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobile ? 0 : 2,
            margin: isMobile ? 0 : 2,
          }
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid #e0e0e0',
            fontSize: isMobile ? '1.25rem' : '1.5rem',
            fontWeight: 'bold',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <School />
          {editingSpecialization ? 'עריכת התמחות' : 'הוספת התמחות חדשה'}
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={isMobile ? 2 : 3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size={isMobile ? 'medium' : 'large'}
                label="שם התמחות *"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size={isMobile ? 'medium' : 'large'}
                label="תיאור"
                multiline
                rows={isMobile ? 3 : 4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="הכנס תיאור מפורט להתמחות"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {formData.is_active ? <Visibility color="primary" /> : <VisibilityOff />}
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formData.is_active ? 'פעיל - יוצג למאמנים' : 'לא פעיל - לא יוצג למאמנים'}
                    </Typography>
                  </Box>
                }
                sx={{ mt: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions
          sx={{
            borderTop: '1px solid #e0e0e0',
            p: isMobile ? 2 : 3,
            gap: 1,
            justifyContent: 'center',
          }}
        >
          <Button 
            onClick={handleCloseDialog}
            size={isMobile ? 'medium' : 'large'}
            sx={{ 
              minWidth: '100px',
              borderRadius: 2,
            }}
          >
            ביטול
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            size={isMobile ? 'medium' : 'large'}
            sx={{
              minWidth: '100px',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
              },
            }}
          >
            {editingSpecialization ? 'עדכן' : 'הוסף'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SpecializationManagement;
